import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import { withDepth } from 'components/filters/useStreamFilters'
import { createOrdinalsBadgeBatcher } from 'hooks/ordinalsBadgeBatch'

const loadOrdinalsBadge = createOrdinalsBadgeBatcher({
    fetchOwners: userIds => api.getOrdinalProfileOwners(userIds),
    fetchOne: async userId => {
        const res = await api.ordinalsMediaStream({
            filter: { user_ordinals: userId },
            page: 1,
            per_page: 1,
        })
        return Array.isArray(res?.items) && res.items.length > 0
    },
})

type Options = {
    enabled?: boolean
    staleTimeMs?: number
}

export default function useUserOrdinalsBadge(
    userId?: string | number | null,
    opts: Options = {},
): {
    hasBadge: boolean
    isLoading: boolean
    isError: boolean
} {
    const id = useMemo(() => {
        const raw = userId === null || userId === undefined ? '' : String(userId).trim()
        return raw || null
    }, [userId])

    const enabled = (opts.enabled ?? true) && !!id
    const staleTime = typeof opts.staleTimeMs === 'number' ? opts.staleTimeMs : 5 * 60 * 1000

    const filterKey = useMemo(() => {
        if (!id) return null
        return serializeFilterKey(withDepth({ user_ordinals: id }))
    }, [id])

    const query = useQuery({
        // Keep base key consistent with the ordinals stream so global invalidations still work.
        queryKey: ['ordinalsMediaStream', filterKey, 'badge'],
        queryFn: async () => {
            if (!id) return false
            return loadOrdinalsBadge(id)
        },
        enabled: enabled && !!filterKey,
        staleTime,
    })

    return {
        hasBadge: !!query.data,
        isLoading: query.isLoading,
        isError: query.isError,
    }
}
